import type { Wallet } from '../economy/wallet';
import { grantCoins, grantMaterial } from '../economy/wallet';
import type { Health } from '../combat/damage';
import type { LootDrop } from './droptable';

const HEART_HEAL_AMOUNT = 25; // flat heal, clamped at maxHp below — not a % so early and late runs both get a meaningful top-up

/**
 * Applies a batch of `PickupPool.update()`'s collected drops — the economy/
 * health side effect of collection, kept separate from the pool's own
 * physics (same split `combat/resolve.ts` keeps between pool-advance and
 * damage-application). `onCosmeticsCollected` is a callback rather than a
 * direct `SaveData` write, matching Wave L-1's own decoupled-from-SaveData
 * discipline (see `economy/drops.ts`'s header) — the actual `save.wardrobe.
 * owned[id] = true` + persistence happens at the call site in main.ts, which
 * has the live `save` reference this module deliberately doesn't import.
 *
 * Review fix (LOW, spec-fidelity): called ONCE with every cosmetic collected
 * THIS tick (was once per cosmetic) — `PickupPool.update()` can return
 * several distinct cosmetic pickups in one physics tick (two nearby reward
 * pickups drifting into magnet range together), and a per-cosmetic call
 * each showing its own toast would clobber the earlier one via `Hud.
 * showToast`'s own deliberate "latest wins immediately" semantics (needed
 * elsewhere — see that method's own comment). Batching the callback lets
 * the caller show ONE combined toast for the whole tick's grants instead of
 * changing `showToast` itself.
 */
export function applyCollectedLoot(drops: LootDrop[], wallet: Wallet, playerHealth: Health, onCosmeticsCollected: (ids: NonNullable<LootDrop['wearableId']>[]) => void): void {
  const cosmetics: NonNullable<LootDrop['wearableId']>[] = [];
  for (const drop of drops) {
    if (drop.kind === 'coin') grantCoins(wallet, 1);
    else if (drop.kind === 'material') grantMaterial(wallet, 'scrap', 1);
    else if (drop.kind === 'heart') playerHealth.hp = Math.min(playerHealth.maxHp, playerHealth.hp + HEART_HEAL_AMOUNT);
    else if (drop.kind === 'part') grantMaterial(wallet, 'part', 1); // pickup amount is always 1 — economy/drops.ts's own rolled `parts` count decides how MANY separate part pickups combat/kill.ts spawns, not a per-pickup amount
    else if (drop.kind === 'cosmetic' && drop.wearableId) cosmetics.push(drop.wearableId);
    // 'collectible' (Wave I, PLAN-CITY3 §5) deliberately has no branch here —
    // it grants nothing through the wallet/health/wardrobe path this
    // function owns. main.ts's own collection loop reads `drop.persistentId`
    // directly to flip `save.progress.placedCollectibles`, since that's a
    // one-off save flag, not a repeatable economy grant this choke point is
    // for.
  }
  if (cosmetics.length > 0) onCosmeticsCollected(cosmetics);
}
